import {
  ApiErrorResponse,
  ApiResponse,
  ContentType,
  EncodingType,
  Integration,
  IntegrationMutableProperties,
  IntegrationTemplate,
  IntegrationSortValues,
  UpdateResult,
} from "./models";
import {
  ApiCall,
  ApiState,
  useApiState,
  useAuthorizedFetch,
  useGetAuthorized,
} from "./useApiState";

const baseConsumerUrl = "int-platform/consumers";
const baseIntegrationUrl = "int-platform/integrations";

export function useIntegrations(
  consumerId: string,
  sorting?: IntegrationSortValues
): ApiState<ApiResponse<Integration[]> | undefined> {
  let modifiedUrl = `${baseConsumerUrl}/${consumerId}/integrations`;

  if (sorting) {
    const { sort, order } = sorting;
    modifiedUrl += `?sort=${sort}&order=${order}`;
  }

  return useApiState(
    useGetAuthorized<ApiResponse<Integration[]>>(
      modifiedUrl,
      undefined,
      undefined,
      undefined,
      "integration-platform"
    ),
    undefined
  );
}

export function useIntegration(
  integrationId: string,
  abortFetch: boolean = false
): ApiState<Integration | undefined> {
  return useApiState(
    useGetAuthorized<Integration>(
      `${baseIntegrationUrl}/${integrationId}`,
      undefined,
      undefined,
      abortFetch,
      "integration-platform"
    ),
    undefined
  );
}

export function useCreateIntegration(
  consumerId: string
): ApiCall<IntegrationMutableProperties, UpdateResult> {
  const authorizedFetch = useAuthorizedFetch(true, "integration-platform");
  return async (integration: IntegrationMutableProperties) => {
    const result = await authorizedFetch(
      `${baseConsumerUrl}/${consumerId}/integrations`,
      {
        method: "post",
        body: JSON.stringify(integration),
      }
    );

    if (!result.ok) {
      const errorResponse: ApiErrorResponse = await result.json();
      return {
        error: true,
        response: errorResponse,
      };
    } else {
      return {
        error: false,
        response: {
          statusCode: result.status,
          data: await result.json().then((data) => data as Integration),
          detail: { message: "Integration created successfully" },
        },
      };
    }
  };
}

export function useUpdateIntegration(
  integrationId: string
): ApiCall<IntegrationMutableProperties, UpdateResult> {
  const authorizedFetch = useAuthorizedFetch(true, "integration-platform");
  return async (integration: IntegrationMutableProperties) => {
    const result = await authorizedFetch(
      `${baseIntegrationUrl}/${integrationId}`,
      { method: "put", body: JSON.stringify(integration) }
    );

    if (!result.ok) {
      const errorResponse: ApiErrorResponse = await result.json();
      return {
        error: true,
        response: errorResponse,
      };
    } else {
      return {
        error: false,
        response: {
          statusCode: result.status,
          data: await result.json().then((data) => data as Integration),
          detail: { message: "Integration updated successfully" },
        },
      };
    }
  };
}

export function useDeleteIntegration(
  integrationId: string
): ApiCall<void, UpdateResult> {
  const authorizedFetch = useAuthorizedFetch(true, "integration-platform");
  return async () => {
    const result = await authorizedFetch(
      `${baseIntegrationUrl}/${integrationId}`,
      {
        method: "delete",
      }
    );
    if (!result.ok) {
      const errorResponse: ApiErrorResponse = await result.json();
      return {
        error: true,
        response: errorResponse,
      };
    } else {
      return {
        error: false,
        response: {
          statusCode: result.status,
          data: await result.json().then((data) => data as Integration),
          detail: { message: "Integration deleted successfully" },
        },
      };
    }
  };
}

export function useIntegrationTemplate(
  integrationId: string,
  abortFetch: boolean = false
): ApiState<IntegrationTemplate | undefined> {
  let modifiedUrl = `${baseIntegrationUrl}/${integrationId}/template`;

  return useApiState(
    useGetAuthorized<IntegrationTemplate>(
      modifiedUrl,
      undefined,
      undefined,
      abortFetch,
      "integration-platform"
    ),
    undefined
  );
}

export function useCreateIntegrationTemplate(integrationId: string): ApiCall<
  {
    content: string;
    encoding: EncodingType;
    contentType: ContentType;
  },
  UpdateResult
> {
  const authorizedFetch = useAuthorizedFetch(true, "integration-platform");
  return async (template: {
    content: string;
    encoding: EncodingType;
    contentType: ContentType;
  }) => {
    const result = await authorizedFetch(
      `${baseIntegrationUrl}/${integrationId}/template`,
      {
        method: "post",
        body: JSON.stringify(template),
      }
    );
    if (!result.ok) {
      const errorResponse: ApiErrorResponse = await result.json();
      return {
        error: true,
        response: errorResponse,
      };
    } else {
      return {
        error: false,
        response: {
          statusCode: result.status,
          detail: { message: "Integration template created successfully" },
        },
      };
    }
  };
}
